import React, { useEffect, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import PropTypes from 'prop-types';
import { getTags } from '../../_redux/actions';

const validate = values => {
  const errors = {};
  if (!values.ibType) errors.ibType = 'Required';
  if (!values.attribute) errors.attribute = 'Required';
  if (values.fxSetting === undefined || values.fxSetting === '') errors.fxSetting = 'Required';
  if (values.metalsSetting === undefined || values.metalsSetting === '') errors.metalsSetting = 'Required';
  if (values.commoditiesSetting === undefined || values.commoditiesSetting === '')
    errors.commoditiesSetting = 'Required';
  if (!values.tag) errors.tag = 'Required';

  ['fxSetting', 'metalsSetting', 'commoditiesSetting'].forEach(fieldName => {
    const value = values[fieldName];
    if (value !== undefined && value !== '' && Number(value) < 0) {
      errors[fieldName] = 'Must be at least 0';
    }
  });

  return errors;
};

const renderTextField = ({ input, label, placeholder, type, step, meta: { touched, error } }) => {
  const currentValue = Number(input.value || 0);
  const isNumber = type === 'number';

  return (
    <div className="LinkDistributionModal__field">
      <label className="LinkDistributionModal__label">
        <span className="LinkDistributionModal__required">*</span>
        {label}
      </label>
      <div className={`LinkDistributionModal__controlWrapper${touched && error ? ' has-error' : ''}`}>
        <input
          {...input}
          className="LinkDistributionModal__input"
          placeholder={placeholder}
          type={type || 'text'}
          step={step}
          min={isNumber ? 0 : undefined}
          value={input.value}
        />
        {isNumber && (
          <div className="LinkDistributionModal__numberControls">
            <button
              type="button"
              className="LinkDistributionModal__numberButton"
              aria-label={`Increase ${label}`}
              title={`Increase ${label}`}
              onClick={() => input.onChange((currentValue + 1).toFixed(2))}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M2.5 7.5L6 3.5l3.5 4" />
              </svg>
            </button>
            <button
              type="button"
              className="LinkDistributionModal__numberButton"
              aria-label={`Decrease ${label}`}
              title={`Decrease ${label}`}
              disabled={currentValue <= 0}
              onClick={() => input.onChange(Math.max(0, currentValue - 1).toFixed(2))}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M2.5 4.5L6 8.5l3.5-4" />
              </svg>
            </button>
          </div>
        )}
        {touched && error && <div className="LinkDistributionModal__error">{error}</div>}
      </div>
    </div>
  );
};

const renderSelectField = ({ input, label, options, placeholder, meta: { touched, error } }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  const selectedOption = options.find(option => String(option.value) === String(input.value));

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = event => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  const handleSelect = option => {
    input.onChange(option.value);
    input.onBlur(option.value);
    setIsOpen(false);
  };

  return (
    <div className="LinkDistributionModal__field">
      <label className="LinkDistributionModal__label">
        <span className="LinkDistributionModal__required">*</span>
        {label}
      </label>
      <div className={`LinkDistributionModal__controlWrapper${touched && error ? ' has-error' : ''}`}>
        <div ref={selectRef} className={`LinkDistributionModal__selectWrapper${isOpen ? ' is-open' : ''}`}>
          <button
            type="button"
            className={`LinkDistributionModal__selectTrigger${selectedOption ? '' : ' is-placeholder'}`}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            onClick={() => setIsOpen(open => !open)}
            onKeyDown={event => {
              if (event.key === 'Escape') setIsOpen(false);
            }}>
            <span>{selectedOption ? selectedOption.label : placeholder}</span>
            <span className="LinkDistributionModal__selectArrow" aria-hidden="true" />
          </button>
          {isOpen && (
            <div className="LinkDistributionModal__selectMenu" role="listbox">
              {options.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={`LinkDistributionModal__selectOption${
                    selectedOption && String(selectedOption.value) === String(option.value) ? ' is-selected' : ''
                  }`}
                  role="option"
                  aria-selected={selectedOption && String(selectedOption.value) === String(option.value)}
                  onClick={() => handleSelect(option)}>
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {touched && error && <div className="LinkDistributionModal__error">{error}</div>}
      </div>
    </div>
  );
};

const LinkDistributionModal = ({ handleSubmit, onClose, tags, getTags, invalid, submitting }) => {
  useEffect(() => {
    getTags();
  }, [getTags]);

  const attributeOptions = [
    { value: 'basic', label: 'Basic' },
    { value: 'mark-up', label: 'Mark-up' },
  ];

  const tagOptions = tags.map(tag => ({ value: tag.id, label: tag.name }));

  return (
    <form onSubmit={handleSubmit} className="LinkDistributionModal">
      <div className="LinkDistributionModal__content">
        <Field
          name="ibType"
          component={renderTextField}
          label="Type"
          placeholder="e.g. Basic / Mark-up 10"
          type="text"
        />
        <Field
          name="attribute"
          component={renderSelectField}
          label="Attribute"
          options={attributeOptions}
          placeholder="Please select"
        />
        <Field name="fxSetting" component={renderTextField} label="FX" type="number" step="1" placeholder="0.00" />
        <Field
          name="metalsSetting"
          component={renderTextField}
          label="METALS"
          type="number"
          step="1"
          placeholder="0.00"
        />
        <Field
          name="commoditiesSetting"
          component={renderTextField}
          label="COMMODITIES"
          type="number"
          step="1"
          placeholder="0.00"
        />
        <Field name="tag" component={renderSelectField} label="Tag" options={tagOptions} placeholder="Please select" />
      </div>
      <div className="LinkDistributionModal__footer">
        <button
          type="button"
          className="LinkDistributionModal__button LinkDistributionModal__button--cancel"
          onClick={onClose}>
          Cancel
        </button>
        <button
          type="submit"
          className="LinkDistributionModal__button LinkDistributionModal__button--save"
          disabled={invalid || submitting}>
          Save
        </button>
      </div>
      <style jsx global>{`
        .LinkDistributionModal__modal {
          width: 600px !important;
          max-width: 90vw !important;
        }

        .LinkDistributionModal__modal .BasicCRMModal__inner {
          width: 100%;
          padding: 24px 28px 18px;
        }

        .LinkDistributionModal__modal .BasicCRMModal__title {
          justify-content: flex-start;
          text-align: left;
          font-size: 20px;
          font-weight: 500;
          line-height: 1.2;
          margin: 0 0 24px;
        }

        .LinkDistributionModal__modal .BasicModal__close {
          top: 24px;
          right: 24px;
        }

        .LinkDistributionModal__content {
          display: flex;
          flex-direction: column;
          gap: 20px;
          padding: 0;
        }

        .LinkDistributionModal__field {
          display: grid;
          grid-template-columns: 140px minmax(0, 420px);
          align-items: flex-start;
          column-gap: 16px;
        }

        .LinkDistributionModal__label {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          min-height: 40px;
          white-space: nowrap;
          text-align: right;
          color: #5f636d;
          font-size: 14px;
          font-weight: 400;
          line-height: 1.35;
        }

        .LinkDistributionModal__required {
          margin-right: 6px;
          color: #ff4d4f;
          font-size: 14px;
          font-weight: 600;
        }

        .LinkDistributionModal__controlWrapper {
          position: relative;
          width: 100%;
        }

        .LinkDistributionModal__input {
          width: 100%;
          height: 40px;
          border: 1px solid #d8dde6;
          border-radius: 6px;
          background: #fff;
          color: #5f636d;
          font-size: 14px;
          font-weight: 400;
          outline: none;
          box-sizing: border-box;
        }

        .LinkDistributionModal__input {
          padding: 0 16px;
        }

        .LinkDistributionModal__input[type='number'] {
          padding-right: 52px;
          text-align: center;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: textfield;
        }

        .LinkDistributionModal__input[type='number']::-webkit-inner-spin-button,
        .LinkDistributionModal__input[type='number']::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        .LinkDistributionModal__numberControls {
          position: absolute;
          top: 1px;
          right: 1px;
          z-index: 2;
          width: 40px;
          height: calc(100% - 2px);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border-left: 1px solid #d8dde6;
          border-radius: 0 5px 5px 0;
          background: #f7f9fc;
        }

        .LinkDistributionModal__numberButton {
          flex: 1 1 50%;
          min-height: 0;
          width: 100%;
          padding: 0;
          border: none;
          background: transparent;
          color: #8b94a3;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .LinkDistributionModal__numberButton:hover,
        .LinkDistributionModal__numberButton:focus-visible {
          background: #e8edf4;
          color: #5f636d;
        }

        .LinkDistributionModal__numberButton:disabled {
          background: #f7f9fc;
          color: #c9ced8;
          cursor: not-allowed;
        }

        .LinkDistributionModal__numberButton + .LinkDistributionModal__numberButton {
          border-top: 1px solid #d8dde6;
        }

        .LinkDistributionModal__input::placeholder {
          color: #b9bec8;
          opacity: 1;
        }

        .LinkDistributionModal__selectWrapper {
          position: relative;
          z-index: 1;
        }

        .LinkDistributionModal__selectWrapper.is-open {
          z-index: 10;
        }

        .LinkDistributionModal__selectTrigger {
          position: relative;
          width: 100%;
          height: 40px;
          padding: 0 48px 0 16px;
          border: 1px solid #d8dde6;
          border-radius: 6px;
          background: #fff;
          color: #5f636d;
          display: flex;
          align-items: center;
          justify-content: space-between;
          text-align: left;
          font-family: inherit;
          font-size: 14px;
          font-weight: 400;
          line-height: 1;
          box-sizing: border-box;
          cursor: pointer;
        }

        .LinkDistributionModal__selectTrigger:hover,
        .LinkDistributionModal__selectTrigger:focus-visible {
          border-color: #4095f7;
        }

        .LinkDistributionModal__selectTrigger.is-placeholder {
          color: #b9bec8;
        }

        .LinkDistributionModal__selectArrow {
          position: absolute;
          top: 50%;
          right: 18px;
          width: 10px;
          height: 10px;
          border-right: 2px solid #b9bec8;
          border-bottom: 2px solid #b9bec8;
          transform: translateY(-65%) rotate(45deg);
          transition: transform 0.2s ease;
        }

        .LinkDistributionModal__selectWrapper.is-open .LinkDistributionModal__selectArrow {
          transform: translateY(-25%) rotate(225deg);
        }

        .LinkDistributionModal__selectMenu {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          max-height: 240px;
          padding: 6px 0;
          overflow-y: auto;
          border: 1px solid #d8dde6;
          border-radius: 6px;
          background: #fff;
          box-shadow: 0 8px 20px rgba(39, 55, 77, 0.14);
        }

        .LinkDistributionModal__selectOption {
          width: 100%;
          min-height: 40px;
          padding: 8px 16px;
          border: none;
          background: transparent;
          color: #5f636d;
          display: block;
          text-align: left;
          font-family: inherit;
          font-size: 14px;
          font-weight: 400;
          line-height: 1.35;
          cursor: pointer;
        }

        .LinkDistributionModal__selectOption:hover,
        .LinkDistributionModal__selectOption:focus-visible,
        .LinkDistributionModal__selectOption.is-selected {
          background: #f1f6fd;
          color: #2f80ed;
        }

        .LinkDistributionModal__controlWrapper.has-error .LinkDistributionModal__input,
        .LinkDistributionModal__controlWrapper.has-error .LinkDistributionModal__selectTrigger {
          border-color: #ff4d4f;
        }

        .LinkDistributionModal__error {
          color: #ff4d4f;
          font-size: 13px;
          margin-top: 4px;
        }

        .LinkDistributionModal__footer {
          display: flex;
          justify-content: flex-end;
          gap: 14px;
          margin-top: 56px;
        }

        .LinkDistributionModal__button {
          min-width: 88px;
          height: 40px;
          border-radius: 6px;
          font-size: 16px;
          font-weight: 400;
          cursor: pointer;
        }

        .LinkDistributionModal__button--cancel {
          border: 1px solid #d8dde6;
          background: #fff;
          color: #5f636d;
        }

        .LinkDistributionModal__button--save {
          border: 1px solid #4095f7;
          background: #4095f7;
          color: #fff;
        }

        .LinkDistributionModal__button[disabled] {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </form>
  );
};

LinkDistributionModal.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  tags: PropTypes.array.isRequired,
  getTags: PropTypes.func.isRequired,
  invalid: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
};

renderTextField.propTypes = {
  input: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  step: PropTypes.string,
  meta: PropTypes.object.isRequired,
};

renderTextField.defaultProps = {
  placeholder: '',
  type: 'text',
  step: undefined,
};

renderSelectField.propTypes = {
  input: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string.isRequired,
  meta: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  tags: state.crm.linkDistribution.tags || [],
});

const mapDispatchToProps = {
  getTags: () => getTags.request(),
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm({
    form: 'LinkDistributionForm',
    validate,
    enableReinitialize: true,
  })
)(LinkDistributionModal);
